import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { GridBaseQuery } from '@core';
import { StatusEntity } from '../../domain/entities/status.entity';
import { StatusDetailEntity } from '../../domain/entities/status-detail.entity';
import { StatusCategoryEntity } from '../../domain/entities/status-category.entity';
import { StatusUsageEntity } from '../../domain/entities/status-usage.entity';
import { StatusApi } from '../api/status.api';
import { StatusMapper } from '../mappers/status.mapper';

export interface CreateStatusParams {
  name: string;
  color: string;
  description: string;
  categoryId: string;
  isActive: boolean;
  sortOrder: number;
}

export interface UpdateStatusParams {
  name: string;
  color: string;
  description: string;
  categoryId: string;
  isActive: boolean;
}

@Injectable({ providedIn: 'root' })
export class StatusRepository {
  private readonly api = inject(StatusApi);
  private readonly mapper = inject(StatusMapper);

  getAllStatuses(): Observable<StatusEntity[]> {
    return this.api.getAllStatuses().pipe(map((result) => this.mapper.toDomainArray(result.data)));
  }

  getStatusesWithPaging(query: GridBaseQuery): Observable<{ data: StatusEntity[]; totalElements: number }> {
    return this.api.getStatusesWithPaging(query).pipe(
      map((result) => ({
        data: (result.data || []).map((dto) => this.mapper.toDomain(dto)),
        totalElements: result.total || 0,
      })),
    );
  }

  getStatusDetail(id: string): Observable<StatusDetailEntity> {
    return this.api.getStatusDetail(id).pipe(map((dto) => this.mapper.toDetailDomain(dto)));
  }

  getCategories(): Observable<StatusCategoryEntity[]> {
    return this.api.getCategories().pipe(map((dtos) => this.mapper.toCategoryDomainArray(dtos)));
  }

  createStatus(params: CreateStatusParams): Observable<{ id: string }> {
    return this.api.createStatus({
      name: params.name,
      color: params.color,
      description: params.description,
      categoryId: params.categoryId,
      isActive: params.isActive,
      sortOrder: params.sortOrder,
    });
  }

  updateStatus(id: string, params: UpdateStatusParams): Observable<void> {
    return this.api.updateStatus(id, {
      name: params.name,
      color: params.color,
      description: params.description,
      categoryId: params.categoryId,
      isActive: params.isActive,
    });
  }

  deleteStatus(id: string): Observable<void> {
    return this.api.deleteStatus(id);
  }

  toggleStatusActive(id: string, isActive: boolean): Observable<void> {
    return this.api.toggleStatusActive(id, { isActive });
  }

  getStatusUsage(id: string): Observable<StatusUsageEntity> {
    return this.api.getStatusUsage(id).pipe(
      map((dto) => this.mapper.toUsageDomain(dto)),
    );
  }
}
